import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser } from '@/shared/types/auth';
import { getAccessToken } from '@/shared/lib/storage/token';

type AuthState = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  permissions: string[];
};

function persistUser(user: AuthUser | null) {
  try {
    if (user) localStorage.setItem('user', JSON.stringify(user));
    else localStorage.removeItem('user');
  } catch {
    // ignore quota / private mode
  }
}

function withDerivedName(user: AuthUser): AuthUser {
  const firstName = String(user.firstName || '').trim();
  const lastName = String(user.lastName || '').trim();
  const full = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (!full) return user;
  return { ...user, name: full };
}

function readPersistedUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

const persistedUser = readPersistedUser();

const initialState: AuthState = {
  user: persistedUser,
  isAuthenticated: Boolean(getAccessToken() && persistedUser),
  permissions: persistedUser?.permissions ?? [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials(
      state,
      action: PayloadAction<{ user: AuthUser; permissions?: string[] }>,
    ) {
      const user = withDerivedName(action.payload.user);
      state.user = user;
      state.permissions =
        action.payload.permissions ?? user.permissions ?? [];
      state.isAuthenticated = true;
      persistUser(user);
    },
    updateUser(state, action: PayloadAction<Partial<AuthUser>>) {
      if (!state.user) return;
      const next = withDerivedName({ ...state.user, ...action.payload });
      if (JSON.stringify(state.user) === JSON.stringify(next)) return;
      state.user = next;
      persistUser(next);
    },
    clearCredentials(state) {
      state.user = null;
      state.permissions = [];
      state.isAuthenticated = false;
      persistUser(null);
    },
    setPermissions(state, action: PayloadAction<string[]>) {
      state.permissions = action.payload;
    },
  },
});

export const { setCredentials, updateUser, clearCredentials, setPermissions } =
  authSlice.actions;
export const authReducer = authSlice.reducer;
